import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { VideoTimeline } from "components/scrapEyeDataDashboard/VideoPlayer/components/VideoTimeline";
import { useAreaLabelledEvents } from "components/scrapEyeLabelling/hooks/useAreaLabel";
import { useScrapEyeMetadata } from "contexts/scrapEyeMetadata";
import { Dayjs } from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useCallback, useMemo } from "react";
import { mapReadOnlyPreviewEvents } from "./contexts/ManualReviewEventContext";
import { useReviewEventsWithExclusions } from "./hooks/useReviewEventsWithExclusions";
import { TimelineExclusionReport } from "./hooks/useTimelineExclusions";
import { defaultExclusionPeriodIds } from "./utils/timelineExclusions";

const PREVIEW_WIDTH_PX = 560;

// The region straddling a review-period boundary, rendered read-only in the usual
// VideoTimeline so a reviewer can see the partner of a pair whose other end sits beyond
// the loaded window, alongside the in-view end. Events are fetched from the standard
// review endpoint for the straddling range, so this does not depend on the page's loaded
// (windowed) events. The boundary is drawn as a divider with the off-window side shaded,
// so the two sides read in sequence and are clearly distinguished.
export const AdjacentPeriodPreview = ({
  location,
  start,
  end,
  boundary,
  inViewSide,
}: {
  location: string;
  start: Dayjs;
  end: Dayjs;
  boundary: Dayjs;
  inViewSide: "before" | "after";
}) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const { default_areas: defaultAreas, reservation } = useScrapEyeMetadata();
  const withAreaLabels = useAreaLabelledEvents(location);

  // The peek must merge like the page it sits in, or it would preview a partner whose
  // bounds and material need not match the row the reviewer is looking at.
  const resolveDefaultExclusions = useCallback(
    (report: TimelineExclusionReport) =>
      defaultExclusionPeriodIds({
        labellerTimelines: report.labeller_timelines,
        defaultTimelineExclusions: report.default_timeline_exclusions,
      }),
    []
  );
  const { resolvedResponse: previewData } = useReviewEventsWithExclusions({
    tenantName,
    location,
    windowStart: start,
    windowEnd: end,
    resolveExclusions: resolveDefaultExclusions,
    probePaddingSeconds: reservation.chunk_seconds,
  });

  const events = useMemo(() => {
    if (!previewData) return [];
    return mapReadOnlyPreviewEvents({
      events: previewData.review_events,
      defaultAreas,
      withAreaLabels,
    });
  }, [previewData, defaultAreas, withAreaLabels]);

  const duration = end.diff(start, "second");
  const boundaryPercent = (boundary.diff(start, "second") / duration) * 100;
  const beforeLabel = inViewSide === "before" ? t("inView") : t("beyondWindow");
  const afterLabel = inViewSide === "after" ? t("inView") : t("beyondWindow");

  return (
    <Box
      sx={{
        width: PREVIEW_WIDTH_PX,
        bgcolor: "background.paper",
        color: "text.primary",
        borderRadius: 1,
        p: 1,
      }}
    >
      {previewData ? (
        <>
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ px: 0.5, mb: 0.5 }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: "fontWeightMedium" }}
              color={
                inViewSide === "before" ? "text.primary" : "text.secondary"
              }
            >
              {beforeLabel}
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontWeight: "fontWeightMedium" }}
              color={inViewSide === "after" ? "text.primary" : "text.secondary"}
            >
              {afterLabel}
            </Typography>
          </Stack>
          <Box sx={{ position: "relative" }}>
            <Box
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                ...(inViewSide === "before"
                  ? { left: `${boundaryPercent}%`, right: 0 }
                  : { left: 0, width: `${boundaryPercent}%` }),
                bgcolor: "grey.200",
                pointerEvents: "none",
                zIndex: 0,
              }}
            />
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <VideoTimeline
                readOnly
                currentTime={0}
                duration={duration}
                events={events}
                videoStart={start}
                onSeek={() => undefined}
                location={location}
              />
            </Box>
            <Box
              sx={{
                position: "absolute",
                top: 0,
                bottom: 22,
                left: `${boundaryPercent}%`,
                borderLeft: "2px dashed",
                borderColor: "text.secondary",
                pointerEvents: "none",
                zIndex: 2,
              }}
            />
          </Box>
        </>
      ) : (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={18} />
        </Box>
      )}
    </Box>
  );
};
